// Configuration service for Carousel Office Add-in
import { Config } from '../models/Config';
import { VALID_MODEL_IDS, DEFAULT_MODEL, MODELS } from '../constants/models';
import { backendSettingsService, DEFAULT_BACKEND_EXCEL_COLORS, } from './backendSettingsService';
export class ConfigService {
    constructor() {
        this.STORAGE_KEY = 'carousel_office_config';
        this.APP_CONFIG_KEY = 'carousel_office_app_config';
        this.excelColorHydrationPromise = null;
        this.config = this.loadConfig();
        if (this.normalizeConfig(this.config)) {
            this.persistConfigLocally();
        }
        this.appConfig = this.loadAppConfiguration();
        void this.flushPendingExcelColorSync();
        void this.hydrateExcelColorsFromBackend();
    }
    loadConfig() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const data = JSON.parse(stored);
                const config = new Config();
                Object.assign(config, data);
                return config;
            }
        }
        catch (error) {
            console.error('Error loading config from localStorage:', error);
        }
        return new Config();
    }
    normalizeConfig(config) {
        let changed = false;
        const normalizeModel = (value) => {
            if (VALID_MODEL_IDS.has(value))
                return value;
            // Map old Anthropic date-stamped IDs to closest new equivalent
            if (value.startsWith("anthropic/claude-opus-4-6"))
                return MODELS.OPUS_4_6;
            if (value.startsWith("anthropic/claude-opus-4-5"))
                return MODELS.OPUS_4_6; // deprecated → upgrade to 4.6
            if (value.startsWith("anthropic/claude-opus-4-1"))
                return MODELS.OPUS_4_6;
            if (value.startsWith("anthropic/claude-opus-4"))
                return MODELS.OPUS_4_6;
            if (value.startsWith("anthropic/claude-sonnet-4-5"))
                return MODELS.SONNET_4_5;
            if (value.startsWith("anthropic/claude-sonnet-4"))
                return MODELS.SONNET_4_5;
            if (value.startsWith("anthropic/claude-3"))
                return MODELS.SONNET_4_5;
            if (value.startsWith("anthropic/claude-haiku-4-5"))
                return MODELS.HAIKU_4_5;
            if (value.startsWith("anthropic/claude-haiku"))
                return MODELS.HAIKU_4_5;
            // Map Gemini models
            if (value.startsWith("gemini/gemini-3-pro"))
                return MODELS.GEMINI_3_PRO;
            if (value.startsWith("gemini/gemini-3-flash"))
                return MODELS.GEMINI_3_FLASH;
            if (value.startsWith("gemini/"))
                return MODELS.GEMINI_3_FLASH;
            // Map all OpenAI models to default
            if (value.startsWith("openai/"))
                return DEFAULT_MODEL;
            // Fallback: any unknown model → default
            return DEFAULT_MODEL;
        };
        const nextChat = normalizeModel(config.carouselChatModel);
        if (nextChat !== config.carouselChatModel) {
            config.carouselChatModel = nextChat;
            changed = true;
        }
        const nextAgent = normalizeModel(config.carouselChatAgentModel);
        if (nextAgent !== config.carouselChatAgentModel) {
            config.carouselChatAgentModel = nextAgent;
            changed = true;
        }
        const nextQuickFix = normalizeModel(config.quickFixModel);
        if (nextQuickFix !== config.quickFixModel) {
            config.quickFixModel = nextQuickFix;
            changed = true;
        }
        return changed;
    }
    loadAppConfiguration() {
        try {
            const stored = localStorage.getItem(this.APP_CONFIG_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        }
        catch (error) {
            console.error('Error loading app configuration:', error);
        }
        // Default configuration for browser environment
        return {
            isDevMode: window.location.hostname === 'localhost',
            apiBaseUrl: 'http://localhost:5000',
            environment: 'development',
        };
    }
    getConfig() {
        return this.config;
    }
    getApplicationConfiguration() {
        return this.appConfig;
    }
    persistConfigLocally() {
        const json = JSON.stringify(this.config);
        localStorage.setItem(this.STORAGE_KEY, json);
    }
    applyExcelColors(colors) {
        this.config.isExcelSemanticFormattingEnabled = Boolean(colors.isExcelSemanticFormattingEnabled);
        this.config.excelApplyColorsOnlyToEmptyCells = Boolean(colors.excelApplyColorsOnlyToEmptyCells);
        this.config.excelHardcodedFontColor = colors.excelHardcodedFontColor;
        this.config.excelCalculationFontColor = colors.excelCalculationFontColor;
        this.config.excelReferenceOtherSheetFontColor = colors.excelReferenceOtherSheetFontColor;
        this.config.excelReferenceOtherWorkbookFontColor = colors.excelReferenceOtherWorkbookFontColor;
        this.config.excelReferenceExternalDataFontColor = colors.excelReferenceExternalDataFontColor;
    }
    getExcelColorPayload() {
        return {
            isExcelSemanticFormattingEnabled: Boolean(this.config.isExcelSemanticFormattingEnabled),
            excelApplyColorsOnlyToEmptyCells: Boolean(this.config.excelApplyColorsOnlyToEmptyCells),
            excelHardcodedFontColor: this.config.excelHardcodedFontColor || DEFAULT_BACKEND_EXCEL_COLORS.excelHardcodedFontColor,
            excelCalculationFontColor: this.config.excelCalculationFontColor || DEFAULT_BACKEND_EXCEL_COLORS.excelCalculationFontColor,
            excelReferenceOtherSheetFontColor: this.config.excelReferenceOtherSheetFontColor ||
                DEFAULT_BACKEND_EXCEL_COLORS.excelReferenceOtherSheetFontColor,
            excelReferenceOtherWorkbookFontColor: this.config.excelReferenceOtherWorkbookFontColor ||
                DEFAULT_BACKEND_EXCEL_COLORS.excelReferenceOtherWorkbookFontColor,
            excelReferenceExternalDataFontColor: this.config.excelReferenceExternalDataFontColor ||
                DEFAULT_BACKEND_EXCEL_COLORS.excelReferenceExternalDataFontColor,
        };
    }
    async syncExcelColorsToBackend() {
        try {
            const saved = await backendSettingsService.putExcelColors(this.getExcelColorPayload());
            this.applyExcelColors(saved);
            this.persistConfigLocally();
        }
        catch (error) {
            // Fail-open: local cache remains authoritative until backend recovers.
            console.warn("[ConfigService] Deferred excel color sync (backend unavailable):", error);
        }
    }
    async flushPendingExcelColorSync() {
        try {
            await backendSettingsService.flushPendingWrites();
        }
        catch (error) {
            console.warn("[ConfigService] Pending backend settings flush skipped:", error);
        }
    }
    async hydrateExcelColorsFromBackend(force = false) {
        if (this.excelColorHydrationPromise && !force) {
            return this.excelColorHydrationPromise;
        }
        this.excelColorHydrationPromise = (async () => {
            try {
                await this.flushPendingExcelColorSync();
                const remoteColors = await backendSettingsService.getExcelColors();
                this.applyExcelColors(remoteColors);
                this.persistConfigLocally();
            }
            catch (error) {
                const cached = backendSettingsService.getCachedExcelColors();
                if (cached) {
                    this.applyExcelColors(cached);
                    this.persistConfigLocally();
                }
                console.warn("[ConfigService] Excel color hydrate fallback to local cache:", error);
            }
        })();
        try {
            await this.excelColorHydrationPromise;
        }
        finally {
            this.excelColorHydrationPromise = null;
        }
    }
    saveConfig() {
        try {
            this.persistConfigLocally();
            console.log('Config saved successfully');
            void this.syncExcelColorsToBackend();
        }
        catch (error) {
            console.error('Error saving config to localStorage:', error);
            throw new Error('Failed to save configuration');
        }
    }
    exportSettingsToFile() {
        try {
            const exportDto = this.config.toExportableDto();
            return JSON.stringify(exportDto, null, 2);
        }
        catch (error) {
            console.error('Error exporting settings:', error);
            throw new Error('Failed to export settings');
        }
    }
    importSettingsFromFile(json) {
        try {
            const exportDto = JSON.parse(json);
            this.config.fromExportableDto(exportDto);
            this.saveConfig();
            console.log('Settings imported successfully');
        }
        catch (error) {
            console.error('Error importing settings:', error);
            throw new Error('Failed to import settings');
        }
    }
    updateAppConfiguration(config) {
        this.appConfig = { ...this.appConfig, ...config };
        try {
            localStorage.setItem(this.APP_CONFIG_KEY, JSON.stringify(this.appConfig));
        }
        catch (error) {
            console.error('Error saving app configuration:', error);
        }
    }
}
// Singleton instance
let configServiceInstance = null;
export function getConfigService() {
    if (!configServiceInstance) {
        configServiceInstance = new ConfigService();
    }
    return configServiceInstance;
}
